import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import * as defaults from "../data/portfolio";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const ContentContext = createContext(null);

// Default content shape — built from the static portfolio.js exports
const buildDefaultContent = () => ({
  profile: defaults.profile,
  heroStats: defaults.heroStats,
  researchInterests: defaults.researchInterests,
  grade10: defaults.grade10,
  grade11: defaults.grade11,
  standardisedTests: defaults.standardisedTests,
  universityPrograms: defaults.universityPrograms,
  featuredInitiative: defaults.featuredInitiative,
  ongoingResearch: defaults.ongoingResearch,
  internships: defaults.internships,
  manuscripts: defaults.manuscripts,
  internshipReports: defaults.internshipReports,
  awards: defaults.awards,
  leadership: defaults.leadership,
  mediaNational: defaults.mediaNational,
  mediaRegional: defaults.mediaRegional,
  navItems: defaults.navItems,
  social: {
    title: "NeeYosi.com",
    tagline:
      "India's first AI-powered election transparency platform — built solo, launched without ads or funding, and used by 130,000 voters in days during the 2026 Tamil Nadu Assembly Elections to research the candidates on their own ballot.",
    heroHeadline1: "\"Nee Yosi\" — ",
    heroHeadline2: "You think.",
    heroHeadline3: "A platform built so every voter can.",
    heroParagraph:
      "India's election affidavits are technically public — buried inside scanned PDFs, in English, in legalese. NeeYosi.com restructures, translates, and summarises them so any voter can know who is asking for their vote.",
    endorsementName: "Girish Mathrubootham — Founder & Chairman, Freshworks (NASDAQ: FRSH)",
    endorsementBody:
      "Posted about NeeYosi.com on his personal LinkedIn profile, recognising the platform's impact on election transparency and civic technology in India.",
  },
  socialImpactStats: [
    { num: "130K+", lab: "Users in days" },
    { num: "4,000+", lab: "Candidates indexed" },
    { num: "2", lab: "Languages (Tamil + English)" },
    { num: "₹0", lab: "Ad spend" },
  ],
  socialFeatures: [
    { icon: "Database", title: "Public Data, Made Readable", desc: "Aggregated official affidavits, criminal records, asset declarations and educational backgrounds for every Tamil Nadu Assembly candidate — sourced directly from Election Commission filings." },
    { icon: "Languages", title: "Tamil + English, Side by Side", desc: "Multilingual interface so any voter can research candidates in the language they think in — not just the language the data was published in." },
    { icon: "Zap", title: "AI-Powered Summaries", desc: "NLP pipelines that turn dense legal affidavits and PDFs into voter-friendly summaries, highlighting flagged criminal cases, declared wealth, and educational background at a glance." },
    { icon: "Vote", title: "Constituency-First Search", desc: "Voters search by their constituency — not by political parties — and see exactly who is on their ballot, ranked transparently with structured data." },
  ],
  socialStory: [],
});

export const ContentProvider = ({ children }) => {
  const [content, setContent] = useState(buildDefaultContent());
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const res = await axios.get(`${API}/content`);
      setContent({ ...buildDefaultContent(), ...res.data });
    } catch (err) {
      console.warn("Failed to load backend content, using defaults", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  return (
    <ContentContext.Provider value={{ content, loading, refresh }}>
      {children}
    </ContentContext.Provider>
  );
};

export const useContent = () => {
  const ctx = useContext(ContentContext);
  if (!ctx) throw new Error("useContent must be used inside ContentProvider");
  return ctx;
};
